import { motion } from "framer-motion";
import { MailCheck, Activity, Music, SpellCheck, Dices, Users, Puzzle, Lightbulb } from "lucide-react";
import { scrollToSignup } from "../../lib/scroll";
import { useCountdown, CountdownDigits } from "./PresaleCountdown";

const EASE = [0.22, 1, 0.36, 1];

// Start webinaru: 10.11.2026, godz. 21:00 (czas polski, CET = UTC+1)
const WEBINAR_START = new Date("2026-11-10T21:00:00+01:00").getTime();

const RIBBON_TEXT =
    "RUCH · MUZYKA · GRAMATYKA · GRA TO NIE NUDA · 8 PROSTYCH AKTYWNOŚCI · ZABAWY PRZY TABLICY · ";

const ACTIVITY_ICONS = [
    { icon: Activity, testid: "hero-activity-icon-1", pos: "right-[3%] top-[33%]", dur: 4.6, delay: 1.0, float: 0 },
    { icon: Music, testid: "hero-activity-icon-2", pos: "right-[8%] top-[41%]", dur: 5.4, delay: 1.12, float: 0.6 },
    { icon: SpellCheck, testid: "hero-activity-icon-3", pos: "right-[2%] top-[49%]", dur: 4.2, delay: 1.24, float: 1.1 },
    { icon: Dices, testid: "hero-activity-icon-4", pos: "right-[7%] top-[58%]", dur: 5.8, delay: 1.36, float: 0.3 },
    { icon: Users, testid: "hero-activity-icon-5", pos: "right-[3%] top-[66%]", dur: 4.9, delay: 1.48, float: 0.9 },
    { icon: Puzzle, testid: "hero-activity-icon-6", pos: "right-[8%] top-[74%]", dur: 5.1, delay: 1.6, float: 1.5 },
    { icon: Lightbulb, testid: "hero-activity-icon-7", pos: "right-[2%] top-[82%]", dur: 4.4, delay: 1.72, float: 0.4 },
];

const TitleLine = ({ children, delay }) => (
    <span className="block overflow-hidden pb-1 -mb-1">
        <motion.span
            className="block"
            initial={{ y: "112%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.9, delay, ease: EASE }}
        >
            {children}
        </motion.span>
    </span>
);

export default function Hero() {
    const countdown = useCountdown(WEBINAR_START);

    return (
        <>
            <section
                id="top"
                data-testid="hero-section"
                className="relative overflow-hidden pt-24 sm:pt-36 pb-16 sm:pb-28 sm:min-h-[92vh] sm:flex sm:items-center"
            >
                {/* zdjęcie: na mobile pas u góry (twarz nic nie zasłania), na desktopie portret po prawej */}
                <div className="relative sm:absolute sm:inset-0 h-[24rem] sm:h-full overflow-hidden">
                    <motion.img
                        src="/assets/gosia.webp"
                        alt="Małgorzata Klecuń — prowadząca webinar"
                        initial={{ opacity: 0, x: 260, rotate: 8 }}
                        animate={{ opacity: 1, x: 0, rotate: 0, scale: [1, 1.05, 1] }}
                        transition={{
                            opacity: { duration: 0.4, delay: 0.15 },
                            x: { type: "spring", stiffness: 55, damping: 13, delay: 0.15 },
                            rotate: { type: "spring", stiffness: 55, damping: 13, delay: 0.15 },
                            scale: { duration: 26, repeat: Infinity, ease: "easeInOut", delay: 1.2 },
                        }}
                        className="h-full w-full object-cover object-[50%_0%] [mask-image:linear-gradient(to_bottom,black_70%,transparent)] sm:[mask-image:none] sm:absolute sm:right-0 sm:bottom-0 sm:w-auto sm:object-contain sm:object-bottom"
                        data-testid="hero-host-image"
                    />
                </div>

                {/* rozjaśnienie pod tekst (tylko desktop, tam zdjęcie jest pod treścią) */}
                <div
                    aria-hidden="true"
                    className="hidden sm:block absolute inset-0 bg-gradient-to-r from-canvas via-canvas/70 to-transparent"
                />
                <div className="hidden sm:block absolute inset-0 hero-grid-bg opacity-60" aria-hidden="true" />

                {/* treść */}
                <div className="relative z-10 max-w-7xl mx-auto w-full px-4 sm:px-8 -mt-14 sm:mt-0">
                    <div className="max-w-2xl flex flex-col items-start">
                        <motion.div
                            initial={{ opacity: 0, y: -14, scale: 0.94 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            transition={{ duration: 0.6, delay: 0.1, ease: EASE }}
                        >
                            <span
                                data-testid="hero-eyebrow-badge"
                                className="inline-flex items-center gap-2.5 rounded-full bg-brand-ink text-white font-mono text-[11px] sm:text-sm tracking-[0.14em] uppercase px-5 py-2.5 shadow-md"
                            >
                                <span className="relative flex h-2.5 w-2.5">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand opacity-75" />
                                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-brand" />
                                </span>
                                Bezpłatny webinar online · zapisy otwarte
                            </span>
                        </motion.div>

                        <h1
                            className="mt-7 text-4xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[1.03] text-brand-ink"
                            data-testid="hero-title-h1"
                        >
                            <TitleLine delay={0.25}>
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand via-brand-deep to-brand-darker">
                                    Ruch, muzyka,
                                </span>
                            </TitleLine>
                            <TitleLine delay={0.37}>GRAmatyka:</TitleLine>
                            <TitleLine delay={0.49}>prosto i skutecznie</TitleLine>
                        </h1>

                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.75, delay: 0.65, ease: EASE }}
                            className="mt-6 text-base sm:text-lg lg:text-xl leading-relaxed text-brand-gray"
                            data-testid="hero-description"
                        >
                            GRA jest częścią gramatyki, nie NUDA! Pokażę Ci 8 prostych
                            aktywności, które łączą naukę gramatyki z ruchem, muzyką
                            i zabawą, z wyraźnym celem językowym. Bez skomplikowanych
                            przygotowań, do dostosowania dla różnych grup wiekowych
                            i poziomów.
                        </motion.p>

                        {!countdown.expired && (
                            <motion.div
                                initial={{ opacity: 0, y: 20, scale: 0.97 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                transition={{ duration: 0.7, delay: 0.8, ease: EASE }}
                                className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-4 rounded-[2rem] bg-white/75 backdrop-blur-xl border border-brand-tint2 px-6 py-5 shadow-[0_25px_60px_-30px_rgba(194,24,91,0.45)]"
                                data-testid="hero-countdown-container"
                            >
                                <p className="font-mono text-[11px] sm:text-xs font-bold tracking-[0.18em] uppercase text-brand-deep">
                                    Do startu webinaru
                                    <span className="block mt-1 font-sans text-sm sm:text-base tracking-normal font-extrabold text-brand-ink">
                                        10.11 (wtorek) · 21:00 · online
                                    </span>
                                </p>
                                <CountdownDigits time={countdown} testIdPrefix="hero-countdown" />
                            </motion.div>
                        )}

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.75, delay: 1, ease: EASE }}
                            className="mt-9"
                        >
                            <button
                                type="button"
                                onClick={scrollToSignup}
                                data-testid="hero-cta-button"
                                className="group relative inline-flex items-center gap-3 rounded-full bg-brand-deep px-8 sm:px-10 py-4 sm:py-5 text-base sm:text-lg font-extrabold text-white shadow-[0_20px_45px_-15px_rgba(194,24,91,0.65),0_0_0_6px_rgba(255,0,140,0.12)] transition-[transform,background-color] duration-200 hover:bg-brand hover:scale-[1.03] active:scale-[0.97]"
                            >
                                Zapisuję się bezpłatnie
                                <span className="grid place-items-center w-7 h-7 rounded-full bg-white/20 transition-transform duration-200 group-hover:translate-y-1">↓</span>
                            </button>
                            <p className="mt-4 flex items-center gap-2 text-sm text-brand-gray" data-testid="hero-email-note">
                                <MailCheck className="w-4 h-4 text-brand" aria-hidden="true" />
                                Zapis zajmie Ci 30 sekund, a link do spotkania wyślemy na Twój e-mail.
                            </p>
                        </motion.div>
                    </div>
                </div>

                {/* akcenty na zdjęciu (desktop) */}
                <div className="hidden sm:block absolute inset-0 z-10 pointer-events-none" data-testid="hero-visual">
                    {/* ikony aktywności z webinaru: ruch, muzyka, gramatyka, gra, pary, łamigłówki, pomysły */}
                    {ACTIVITY_ICONS.map((a) => (
                        <motion.div
                            key={a.testid}
                            initial={{ opacity: 0, scale: 0, rotate: -30 }}
                            animate={{ opacity: 1, scale: 1, rotate: 0 }}
                            transition={{ type: "spring", stiffness: 320, damping: 14, delay: a.delay }}
                            className={`absolute ${a.pos}`}
                            data-testid={a.testid}
                        >
                            <motion.div
                                animate={{ y: [0, -14, 0], rotate: [-8, 8, -8] }}
                                transition={{ duration: a.dur, repeat: Infinity, ease: "easeInOut", delay: a.float }}
                                className="grid place-items-center w-14 h-14 lg:w-16 lg:h-16 rounded-2xl bg-white/85 backdrop-blur-md border-2 border-brand-tint2 text-brand-deep shadow-[0_18px_40px_-14px_rgba(196,0,111,0.5)] rotate-3"
                            >
                                <a.icon className="w-7 h-7 lg:w-8 lg:h-8" aria-hidden="true" />
                            </motion.div>
                        </motion.div>
                    ))}

                    {/* tabliczka prowadzącej */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.65, delay: 0.9, ease: EASE }}
                        className="absolute right-6 lg:right-14 bottom-8"
                        data-testid="hero-float-card-host"
                    >
                        <motion.div
                            animate={{ y: [0, 8, 0] }}
                            transition={{ duration: 6.5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                            className="rounded-3xl bg-white/85 backdrop-blur-xl border border-brand-tint2 px-8 py-4 text-center shadow-[0_25px_60px_-20px_rgba(35,38,43,0.35)]"
                        >
                            <p className="font-mono text-xs sm:text-sm font-bold tracking-[0.14em] uppercase text-brand">Prowadzi</p>
                            <p className="mt-1 text-lg sm:text-xl font-extrabold text-brand-ink">Małgorzata Klecuń</p>
                        </motion.div>
                    </motion.div>

                    {/* wyraźna plakietka: udział bezpłatny */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0, rotate: -40 }}
                        animate={{ opacity: 1, scale: 1, rotate: 0 }}
                        transition={{ type: "spring", stiffness: 300, damping: 13, delay: 1.85 }}
                        className="absolute top-28 right-6 lg:right-12"
                        data-testid="hero-float-badge-free"
                    >
                        <motion.div
                            animate={{ y: [0, -9, 0] }}
                            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                            className="grid place-items-center w-32 h-32 sm:w-36 sm:h-36 rounded-full bg-brand-deep text-white text-center shadow-[0_22px_50px_-14px_rgba(143,0,80,0.75)] ring-4 ring-white/80"
                        >
                            <div>
                                <p className="text-3xl sm:text-4xl font-extrabold leading-none">0 zł</p>
                                <p className="mt-1.5 font-mono text-[10px] sm:text-[11px] font-bold tracking-[0.14em] uppercase">
                                    Udział bezpłatny
                                </p>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            {/* wstęga: webinar w ruchu */}
            <div
                className="relative overflow-hidden bg-brand-deep py-4 sm:py-5 -rotate-1 scale-[1.02] shadow-[0_20px_50px_-20px_rgba(143,0,80,0.6)]"
                data-testid="grammar-ribbon"
            >
                <div
                    className="animate-marquee whitespace-nowrap font-mono text-lg sm:text-2xl font-bold tracking-[0.12em] uppercase text-white"
                    style={{ animationDuration: "32s" }}
                >
                    <span>{RIBBON_TEXT.repeat(3)}</span>
                    <span>{RIBBON_TEXT.repeat(3)}</span>
                </div>
            </div>
        </>
    );
}
